const md = `# Sharing demos externally — invitation-only

Public anonymous share links were retired on **2026-05-26** to protect
Eyvo's intellectual property.  All external sharing now happens
through **invitation-only email delivery**.

## How invitations work (60-second explainer)

1. Pick a completed run on **/runs/:id**.
2. Click **Share with viewers** and email-invite up to 50 recipients.
3. Each recipient receives a personal email with a unique magic link.
4. When they click the link, they land on a watermarked player that
   logs every view event back to your invitation timeline.
5. You can **revoke** or **resend** any invitation at any time.

A recipient can re-open the same link as many times as they like
within the expiry window.  The first device that opens it does
**not** get exclusive access — instead, you watch the audit feed
for link-sharing patterns and revoke if anything looks off.

## Sending invitations

1. Open the run detail page.
2. Click **Share with viewers** (only available once the final video
   is ready).
3. Paste one or more email addresses — one per line, comma, or
   semicolon separated.  Up to 50 recipients per send.
4. Optionally add a **company name** (shown in the player watermark)
   and a **custom message** (up to 280 chars, included in the email).
5. Pick an expiry (1 / 7 / 14 / 30 days) — default 14.
6. Leave **Confidentiality acknowledgment required** checked unless
   you have a specific reason to skip it.
7. Click **Send invitations** — you'll see per-recipient ✓ Sent or
   ✗ Failed status inline.

## What viewers see

- A **branded landing page** with the demo title, their own email,
  the company name you set, and the access-expiry date.
- A **confidentiality acknowledgment modal** they must accept before
  playback (if you left the toggle checked).
- A **custom player** with play / pause, seek, volume, mute, and
  fullscreen.  No download button, no speed control, no
  picture-in-picture, and right-click is disabled on the video.
- A **diagonal watermark** with the viewer's email, the company
  name, and a timestamp that refreshes every 30 s — visible during
  normal playback and during fullscreen.
- A **footer** with the Eyvo logo and "Powered by Eyvo Demo Agent"
  — no internal links, no nav bar.

## Revoking or resending

In the **Invitations** table beneath the share button:

- **Resend** — rotates the magic link and sends a fresh email.  The
  previous link immediately stops working.  Up to 3 resends per
  invitation per 24 h.
- **Revoke** — irreversibly invalidates the invitation.  The viewer
  is kicked from the player within ~30 s (on the next heartbeat or
  range request).  A "Your viewing session has expired" message is
  shown; there is no retry button.

## What's audited

Every invitation has a full timeline:

- \`invitation_sent\` — email queued at SendGrid
- \`invitation_resent\` — email re-sent with a fresh link
- \`invitation_opened\` — magic link consumed, viewer cookie set
- \`video_loaded\` — first range request reached the stream endpoint
- \`video_started\` — recipient pressed play
- \`video_paused\` / \`video_resumed\` — pause toggle
- \`video_heartbeat\` — every 10 s while playing
- \`video_completed\` — playback reached end (or tab closed mid-play)
- \`confidentiality_acknowledged\` — agreed to the ack modal
- \`access_revoked\` — operator clicked Revoke
- \`expired_attempt\` — link clicked after expiry
- \`revoked_attempt\` — link clicked after revoke
- \`token_rotated\` — magic-link or session token rotated
- \`sendgrid_send_failed\` — non-2xx from SendGrid

Each event captures IP, user-agent, viewer-session-id (where
applicable), and playback position (for play/pause/heartbeat).

## IP-protection guarantees

What the recipient **never** sees:

- Your workflow ID, name, or version.
- The list of steps, selectors, or variables that produced the run.
- Any per-step screenshots, narration text, or audit events.
- Other internal user emails or operator identities.
- The raw \`final_video\` asset URL — the player streams from a
  cookie-gated \`/api/v/stream/{invitation_id}\` endpoint that
  refuses any request without a live viewer session.
- A download button or right-click "Save as" — both are disabled.

What the recipient **does** see:

- The demo title, their email, the company name you set, and the
  access-expiry date.
- The video itself, streamed in 256 KiB byte ranges with strict
  \`no-store\`, \`X-Robots-Tag: noindex\`, \`Referrer-Policy:
  no-referrer\` headers.

If you spot anything suspicious in the audit timeline, click
**Revoke**.  Revocation propagates within one heartbeat (~10 s) or
one range request, whichever comes first.
`;

export default md;
